/**
 * Where to send someone who wants the Chrome extension.
 *
 * The extension is the reason the product exists — a panel that lives inside
 * Cliniko/Jane/Nookal/Power Diary/Halaxy so staff never leave the PMS to chat.
 * The web app is the fallback surface, not the destination, so the install
 * path has to be reachable from signup and from inside the app.
 *
 * Two install routes, deliberately:
 *
 * - **Chrome Web Store** — one click, auto-updates, no developer mode. This is
 *   the only route a clinic receptionist will actually complete. Set
 *   `VITE_CHROME_WEBSTORE_URL` at build time to enable it.
 * - **Signed zip + load unpacked** — needs `chrome://extensions`, developer
 *   mode, and a manual re-install per update. Kept working unconditionally
 *   because self-hosted deployments have no store listing to point at, and the
 *   self-host path must stay whole (see CLAUDE.md § Constraints).
 *
 * The zip is served by the web container at the path below
 * (packages/web/Dockerfile), so it always matches the deployed build.
 */

/** Public Chrome Web Store listing, when this deployment has one. */
export const CHROME_WEBSTORE_URL = (import.meta.env.VITE_CHROME_WEBSTORE_URL ?? '').trim();

/** Always-available fallback — the same zip the web image was built with. */
export const EXTENSION_ZIP_PATH = '/clinikchat-extension.zip';

/** True when a one-click store install is configured for this deployment. */
export const hasWebStoreListing = CHROME_WEBSTORE_URL.length > 0;

/**
 * Remembers that the user has been shown the install page at least once.
 *
 * The web app cannot detect whether the extension is actually installed —
 * `externally_connectable` is deliberately not set on the manifest, so a page
 * has no channel to ask. Rather than pin the onboarding checklist open forever
 * on an item that can never satisfy itself, treat "visited the install page"
 * as done. Worst case a user re-reads instructions they have already seen.
 */
const SEEN_KEY = 'clinikchat-extension-install-seen';

export function markExtensionInstallSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, new Date().toISOString());
  } catch {
    // Private-browsing / storage-disabled: the checklist item just stays
    // unticked. Never let a bookkeeping write break the install link itself.
  }
}

export function hasSeenExtensionInstall(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) !== null;
  } catch {
    return false;
  }
}
